import clsx from "clsx";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { DetailedHTMLProps, FC, HTMLAttributes } from "react";

import { Cta } from "../../../buttons/cta";
import { RichText } from "../../../rich-text";

export interface BrandCardProps
  extends Omit<Queries.ImageCardDataFragment, "variant">,
    Pick<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "className"> {}

export const BrandCard: FC<BrandCardProps> = ({
  title,
  description,
  image,
  logo,
  ctas,
  className,
}) => {
  const desktopCardBody = (
    <div className={clsx("flex flex-col justify-between h-full w-full")}>
      <div className="text-white">
        {logo?.gatsbyImageData && (
          <GatsbyImage image={logo.gatsbyImageData} alt="" className="mt-8 ml-6 mb-4 invisible" />
        )}
        <h3 className="font-medium text-4xl mb-2 ml-6">{title}</h3>
        {description?.raw && (
          <RichText raw={description.raw} className="font-light xl:text-xl mx-6 my-4" />
        )}
      </div>
      <div>
        {(ctas ?? []).length > 0 && (
          <div className="flex flex-col gap-4 h-auto ml-6 mb-6">
            {ctas
              ?.filter(Boolean)
              .map((cta, index) => <Cta key={index} variant="light" {...cta!} />)}
          </div>
        )}
      </div>
    </div>
  );
  return (
    <>
      {/* Mobile version */}
      <div
        className={clsx(
          "h-full lg:hidden relative",
          "bg-primary text-white",
          "rounded-xl overflow-hidden",
          "flex flex-col",
          className
        )}
      >
        {logo?.gatsbyImageData && (
          <div className="absolute top-0 left-0 z-30 w-full pt-6 px-8">
            <GatsbyImage image={logo.gatsbyImageData} alt="" />
          </div>
        )}

        {/* main background image */}
        {image?.gatsbyImageData && (
          <GatsbyImage
            image={image.gatsbyImageData}
            alt={title ?? ""}
            className="w-full aspect-[16/9]"
          />
        )}

        <div className="p-6">
          <h3 className="font-medium text-4xl">{title}</h3>
          {description?.raw && <RichText raw={description.raw} className="font-light" />}
          {(ctas ?? []).length > 0 && (
            <div className="flex flex-col gap-4 h-auto mt-4">
              {ctas
                ?.filter(Boolean)
                .map((cta, index) => <Cta key={index} variant="light" {...cta!} />)}
            </div>
          )}
        </div>
      </div>
      {/* Desktop version */}
      <div
        className={clsx(
          "relative rounded-xl overflow-hidden group hidden lg:block h-full",
          className
        )}
      >
        <div className="invisible">
          {logo?.gatsbyImageData && (
            <div className="max-w-full mt-8 ml-6">
              <GatsbyImage image={logo.gatsbyImageData} alt="" className="m-4" />
            </div>
          )}
          {desktopCardBody}
        </div>

        {logo?.gatsbyImageData && (
          <div className="absolute top-0 left-0 z-30 mt-12 px-10 max-w-full">
            <GatsbyImage image={logo.gatsbyImageData} alt="" />
          </div>
        )}

        <div className="absolute z-20 bottom-0 px-8 w-full h-full">
          {/* Bottom overlay layer: background and secondary image */}
          <span className="absolute bottom-0 left-0 transition-all group-hover:h-full w-full bg-primary -z-10 h-0" />
          <span
            className={clsx(
              "absolute -bottom-10 left-0 h-0 transition-all w-full",
              "group-hover:h-full group-hover:bottom-0 p-4"
            )}
          >
            {desktopCardBody}
          </span>
        </div>

        {/* Gradient */}
        <div className="absolute top-0 left-0 z-10 w-full h-full bg-gradient-to-b from-transparent to-[rgba(0,0,0,0.5)]" />

        {/* main background image */}
        {image?.gatsbyImageData && (
          <GatsbyImage
            image={image.gatsbyImageData}
            alt={title ?? ""}
            objectFit="cover"
            className="w-full h-full !absolute left-0 top-0"
          />
        )}
      </div>
    </>
  );
};
